import React, { useState, useEffect } from 'react';
import { DeviceSplitDonut, NewVsReturningBar } from './ChartsSection';
import InfoTip from '../shared/InfoTip';
import ThinScroll from '../shared/ThinScroll';
import { fmtNumber, fmtPercent, fmtDuration } from './shared/format';
import { downloadCsv, downloadPdf, makeExport, SearchBar, ExportMenu, SectionInsightCard } from './shared';
import { TechTabContent, getTechSummary } from './ContentBehaviorTabs';
import TrafficOverviewTab, { exportTrafficCsv, exportTrafficPdf } from './traffic-acquisition/TrafficOverviewTab';
import ChannelsTab, { exportChannelsCsv, exportChannelsPdf } from './traffic-acquisition/ChannelsTab';
import SourcesTab, { exportSourcesCsv, exportSourcesPdf } from './traffic-acquisition/SourcesTab';
import EventsTab, { exportEventsCsv, exportEventsPdf } from './traffic-acquisition/EventsTab';

// One column spec drives CSV + PDF (can't drift). CSV emits raw values + full
// headers; PDF emits formatted strings + abbreviated headers.
const TECH_EXPORT = makeExport([
  { header: 'Device', get: (row) => row.device },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'New Users', get: (row) => row.new_users, pdf: (row) => fmtNumber(row.new_users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Avg Duration', pdfHeader: 'Dur.', get: (row) => row.avg_session_duration, pdf: (row) => fmtDuration(row.avg_session_duration) },
  { header: 'Pageviews', pdfHeader: 'PVs', get: (row) => row.pageviews, pdf: (row) => fmtNumber(row.pageviews) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

const exportTechCsv = (devicesData, rangeStart, rangeEnd) => {
  if (!devicesData?.length) return;
  downloadCsv(TECH_EXPORT.headers, devicesData.map(TECH_EXPORT.toCsvRow), `ga4-tech-${rangeStart}-to-${rangeEnd}.csv`);
};

const exportTechPdf = (devicesData, rangeStart, rangeEnd) => {
  if (!devicesData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  downloadPdf('Technology', TECH_EXPORT.pdfHeaders, devicesData.map(TECH_EXPORT.toPdfRow), `ga4-tech-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

const TABS = [
  { id: 'traffic', label: 'Traffic Overview', tip: 'Day-by-day metrics for the selected range, plus channel breakdown, top landing pages, and device split as compact cards.' },
  { id: 'channels', label: 'Channels', tip: "Sessions and conversions grouped by GA4's default channel grouping (Organic, Paid, Direct, Social, Referral, Other). Use to compare which channels drive traffic vs. which actually convert." },
  { id: 'sources', label: 'Sources', tip: 'Sessions and conversions by source/medium pair (e.g. google/organic, facebook/cpc). More granular than Channels — useful for tracking individual campaigns.' },
  { id: 'events', label: 'Events', tip: 'All GA4 events fired during the range — counts, unique users, and event value. Click an event to see daily conversion trend and conversion rate.' },
  { id: 'tech', label: 'Tech', tip: 'Sessions and bounce rate by device category, browser, and OS. Use to spot a bad mobile experience or a broken browser.' },
];

const TrafficAcquisitionTabs = ({
  trafficData, trafficLoading, trafficError,
  channelData, channelLoading, channelError, channelTrendData, channelTrendError,
  sourceMediumData, sourceMediumLoading, sourceMediumError, sourceMediumTrendData,
  campaignsData, campaignsLoading, campaignsError,
  eventsData, eventsLoading, eventsError, conversionsData, conversionsLoading, conversionsError,
  selectedClientId, rangeStart, rangeEnd,
  hourlyTrafficData, hourlyTrafficLoading, hourlyTrafficError, hourlyTrafficWindows, selectedHourlyTrafficWindow, onHourlyTrafficWindowChange,
  dayOfWeekData, dayOfWeekLoading, dayOfWeekError, dayOfWeekWindows, selectedDayOfWeekWindow, onDayOfWeekWindowChange,
  devicesData, devicesLoading, devicesError, browsersData, browsersLoading, browsersError, browserWindows, selectedBrowserWindow, onBrowserWindowChange,
  osData, osLoading, osError, osWindows, selectedOsWindow, onOsWindowChange,
  userTypeData, userTypeLoading,
}) => {
  const [activeTab, setActiveTab] = useState('traffic');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { setSearchQuery(''); }, [activeTab]);

  const tabExports = {
    traffic: {
      csv: () => exportTrafficCsv(trafficData, rangeStart, rangeEnd),
      pdf: () => exportTrafficPdf(trafficData, rangeStart, rangeEnd),
    },
    channels: {
      csv: () => exportChannelsCsv(channelData, rangeStart, rangeEnd),
      pdf: () => exportChannelsPdf(channelData, rangeStart, rangeEnd),
    },
    sources: {
      csv: () => exportSourcesCsv(sourceMediumData, rangeStart, rangeEnd),
      pdf: () => exportSourcesPdf(sourceMediumData, rangeStart, rangeEnd),
    },
    events: {
      csv: () => exportEventsCsv(eventsData, rangeStart, rangeEnd),
      pdf: () => exportEventsPdf(eventsData, rangeStart, rangeEnd),
    },
    tech: {
      csv: () => exportTechCsv(devicesData, rangeStart, rangeEnd),
      pdf: () => exportTechPdf(devicesData, rangeStart, rangeEnd),
    },
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-5">
      {/* Tab bar */}
      <div className="flex items-center justify-between flex-wrap gap-x-3 gap-y-2 border-b dark:border-gray-700 mb-4">
        <ThinScroll className="flex items-center gap-1 overflow-x-auto pb-1.5 md:pb-0 -mb-px" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-[#32B1FF] text-[#32B1FF]'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && tab.tip && <InfoTip text={tab.tip} />}
            </button>
          ))}
        </ThinScroll>
        <div className="flex items-center gap-2 pb-1 flex-wrap max-[589px]:w-full">
          <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={`Search ${TABS.find(tab => tab.id === activeTab)?.label ?? ''}…`} />
          <ExportMenu onCsv={tabExports[activeTab].csv} onPdf={tabExports[activeTab].pdf} />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'traffic' && (
        <TrafficOverviewTab
          trafficData={trafficData}
          trafficLoading={trafficLoading}
          trafficError={trafficError}
          channelData={channelData}
          selectedClientId={selectedClientId}
          searchQuery={searchQuery}
          hourlyTrafficData={hourlyTrafficData}
          hourlyTrafficLoading={hourlyTrafficLoading}
          hourlyTrafficError={hourlyTrafficError}
          hourlyTrafficWindows={hourlyTrafficWindows}
          selectedHourlyTrafficWindow={selectedHourlyTrafficWindow}
          onHourlyTrafficWindowChange={onHourlyTrafficWindowChange}
          dayOfWeekData={dayOfWeekData}
          dayOfWeekLoading={dayOfWeekLoading}
          dayOfWeekError={dayOfWeekError}
          dayOfWeekWindows={dayOfWeekWindows}
          selectedDayOfWeekWindow={selectedDayOfWeekWindow}
          onDayOfWeekWindowChange={onDayOfWeekWindowChange}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
        />
      )}
      {activeTab === 'channels' && (
        <ChannelsTab
          channelData={channelData}
          channelLoading={channelLoading}
          channelError={channelError}
          channelTrendData={channelTrendData}
          channelTrendError={channelTrendError}
          searchQuery={searchQuery}
        />
      )}
      {activeTab === 'sources' && (
        <SourcesTab
          sourceMediumData={sourceMediumData}
          sourceMediumLoading={sourceMediumLoading}
          sourceMediumError={sourceMediumError}
          sourceMediumTrendData={sourceMediumTrendData}
          campaignsData={campaignsData}
          campaignsLoading={campaignsLoading}
          campaignsError={campaignsError}
          searchQuery={searchQuery}
        />
      )}
      {activeTab === 'events' && (
        <EventsTab
          eventsData={eventsData}
          eventsLoading={eventsLoading}
          eventsError={eventsError}
          conversionsData={conversionsData}
          conversionsLoading={conversionsLoading}
          conversionsError={conversionsError}
          selectedClientId={selectedClientId}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          searchQuery={searchQuery}
          trafficData={trafficData}
          trafficError={trafficError}
        />
      )}
      {activeTab === 'tech' && (
        <div>
          <SectionInsightCard title="Tech Insight" {...getTechSummary(devicesData)} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DeviceSplitDonut data={devicesData} loading={devicesLoading} />
            <NewVsReturningBar data={userTypeData} loading={userTypeLoading} />
          </div>
          <TechTabContent
            devicesData={devicesData}
            devicesLoading={devicesLoading}
            devicesError={devicesError}
            browsersData={browsersData}
            browsersLoading={browsersLoading}
            browsersError={browsersError}
            browserWindows={browserWindows}
            selectedBrowserWindow={selectedBrowserWindow}
            onBrowserWindowChange={onBrowserWindowChange}
            osData={osData}
            osLoading={osLoading}
            osError={osError}
            osWindows={osWindows}
            selectedOsWindow={selectedOsWindow}
            onOsWindowChange={onOsWindowChange}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            searchQuery={searchQuery}
          />
        </div>
      )}
    </div>
  );
};

export default TrafficAcquisitionTabs;
